// Desktop UI primitives (feature 007, T063).
//
// Re-expressed from `SVTrip_Design_System/components/core/` against the shared
// Tailwind token mirror. The kit renders through a global namespace with inline
// styles and cannot be imported, which is the gap Principle VI's living-artifact
// clause asks to be recorded (see T116).
//
// Deliberately NOT shared with the mobile client. `@svtrip/core` carries no
// layout-bearing components precisely so a change made here for a wide screen
// can never alter the mobile app's appearance (FR-040). The two interfaces share
// tokens and icons — the things that must not drift — and nothing else.
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from '@svtrip/core/Icon';

export function cx(...parts: Array<string | false | undefined | null>): string {
  return parts.filter(Boolean).join(' ');
}

/* ── Button ──────────────────────────────────────────────────────────────── */

type Variant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'dark';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-sunset text-white shadow-red',
  secondary: 'bg-surface text-text ring-[1.5px] ring-inset ring-border',
  accent: 'bg-gold-gradient text-navy shadow-gold',
  ghost: 'bg-transparent text-primary',
  dark: 'bg-navy text-white shadow-md',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm gap-1.5',
  md: 'h-[46px] px-[22px] text-[15px] gap-2',
  lg: 'h-14 px-7 text-[17px] gap-2.5',
};

// `whitespace-nowrap` is load-bearing: these are fixed-height pills, so a label
// that wraps overflows the button instead of growing it.
const BTN_BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-pill font-extrabold leading-none ' +
  'transition hover:brightness-105 active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

interface ButtonBits {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  iconLeft?: IconName;
  fullWidth?: boolean;
  className?: string;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  iconLeft,
  fullWidth,
  className,
  ...rest
}: ButtonBits & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cx(BTN_BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={size === 'lg' ? 20 : 18} />}
      {children}
    </button>
  );
}

export function ButtonLink({
  children,
  variant = 'primary',
  size = 'md',
  iconLeft,
  fullWidth,
  className,
  ...rest
}: ButtonBits & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className={cx(BTN_BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={size === 'lg' ? 20 : 18} />}
      {children}
    </a>
  );
}

/* ── Inputs ──────────────────────────────────────────────────────────────── */

const FIELD =
  'w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-text outline-none ' +
  'transition placeholder:text-muted focus:border-primary';

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(FIELD, props.className)} />;
}


/**
 * A native select, styled from the same token string every other input uses.
 *
 * The first component of its kind in the product (feature 011). The country
 * list has ~249 options, and every existing choice pattern here — pills, chips,
 * segmented toggles — is built for small fixed sets: 249 pills is unusable, and
 * on a 390px phone it is not even scrollable in any sensible way.
 *
 * Native rather than a custom listbox on purpose. The platform picker already
 * handles keyboard navigation, type-ahead, and the full-screen wheel a phone
 * shows for long lists — all of which a hand-rolled version would have to
 * reimplement and would get subtly wrong. `SVTrip_Design_System/` defines no
 * select pattern; this extends its tokens rather than inventing a visual
 * language, and the gap is recorded in the feature plan so the system can
 * absorb it.
 */
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(FIELD, 'appearance-none pr-9', props.className)} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={4} {...props} className={cx(FIELD, 'resize-y', props.className)} />;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-bold text-muted">{label}</span>
      {children}
    </label>
  );
}

/** Pill search input from `ui_kits/web/WebNav.jsx`. */
export function SearchInput({
  label,
  ...props
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
        <Icon name="search" size={18} />
      </span>
      <input
        type="search"
        aria-label={label}
        {...props}
        className={cx(
          'h-11 w-full rounded-pill border border-border bg-surface pl-10 pr-4 text-text outline-none',
          'transition placeholder:text-muted focus:border-primary',
          props.className,
        )}
      />
    </div>
  );
}

/* ── Surfaces ────────────────────────────────────────────────────────────── */

/** Borderless, soft warm-navy shadow. The design system bans bordered cards. */
export function Card({
  children,
  className,
  role,
}: {
  children: ReactNode;
  className?: string;
  role?: string;
}) {
  return (
    <div role={role} className={cx('rounded-lg bg-surface shadow-md', className)}>
      {children}
    </div>
  );
}

/**
 * Status pill, mirrored from `SVTrip_Design_System/components/core/Badge.jsx`.
 *
 * Feature 018 needed it for the six subscription states and found that NEITHER
 * surface had mirrored it — which is why `SubscriptionScreen`'s "Recomendado"
 * pill was hand-rolled as `absolute rounded-pill bg-gold-gradient px-3 py-0.5`.
 * That is the parallel pattern Principle VI forbids, and it would have been
 * copied six more times.
 *
 * ONE DEVIATION FROM THE DESIGN SYSTEM, recorded rather than silent: the DS
 * Badge keys its tones off semantic colors (`--success-bg`, `--warning-bg`,
 * `--danger-bg`, `--info-bg`) that this product's token mirror has never
 * carried — `core/tailwind.preset.ts` defines bg/surface/surface-2/border/text/
 * muted/primary/primary-fg/accent/navy and nothing else. Mirroring those tones
 * faithfully would mean adding seven colors to the preset all three surfaces
 * import, for a status pill.
 *
 * So the SHAPE is the DS's exactly — pill radius, 24px tall, 800 12px/1 body
 * font, optional leading icon or dot — and the tones map onto the tokens the
 * product already uses for state: `primary` for live (the idiom
 * `PublicationChecklist` already uses for "ready"), the gold gradient for
 * something time-limited, `navy` for something needing action, `muted` for
 * something switched off. Four tones carry six states; the LABEL carries the
 * precision, which is the honest division when there is no semantic palette.
 *
 * The gap is flagged in `specs/018-merchant-subscription-plans/design-review.md`
 * so the design system can decide whether the product mirror should grow
 * semantic colors.
 */
export type BadgeTone = 'live' | 'promo' | 'attention' | 'off';

export function Badge({
  children,
  tone = 'off',
  icon,
  dot = false,
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  icon?: IconName;
  dot?: boolean;
  className?: string;
}) {
  const styles: Record<BadgeTone, string> = {
    live: 'bg-surface-2 text-primary',
    promo: 'bg-gold-gradient text-navy shadow-gold',
    attention: 'bg-navy text-white',
    off: 'bg-surface-2 text-muted',
  };
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 text-xs font-extrabold',
        styles[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

export function Chip({
  children,
  active,
  onClick,
  iconLeft,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  /**
   * Either an icon NAME from the brand line set, or a rendered node — which is
   * how a Material Symbols glyph gets in (feature 014). Two icon sets now
   * coexist, and a chip that only accepted one would quietly exclude every
   * admin-assigned icon from whatever it labels.
   */
  iconLeft?: IconName | ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-pill border px-3.5 py-2 text-sm font-bold transition',
        active ? 'border-transparent bg-sunset text-white shadow-red' : 'border-border bg-surface text-text',
      )}
    >
      {typeof iconLeft === 'string' ? <Icon name={iconLeft as IconName} size={14} /> : iconLeft}
      {children}
    </button>
  );
}

/* ── The three states every screen owes the visitor (FR-016) ─────────────── */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12" role="status" aria-live="polite">
      <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-border border-t-primary" />
      {label && <span className="text-sm font-bold text-muted">{label}</span>}
    </div>
  );
}

export function EmptyState({
  icon = 'compass',
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-2 text-muted">
        <Icon name={icon} size={24} />
      </span>
      <p className="font-display text-lg font-extrabold text-text">{title}</p>
      {body && <p className="max-w-sm text-sm text-muted">{body}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center" role="alert">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-2 text-primary">
        <Icon name="bell" size={24} />
      </span>
      <p className="font-display text-lg font-extrabold text-text">{message}</p>
      {action}
    </div>
  );
}
